import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Alert,
  ListItemIcon,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  styled,
  IconButton,
  useTheme,
  Chip,
  Stack,
} from '@mui/material';
import { DragDropContext, Droppable, Draggable, DroppableProps } from 'react-beautiful-dnd';
import { DragIndicator, MoreVert } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { AGGREGATOR_OPTIONS, getAggregatorLogo } from '../constants/icons';
import {
  MerchantConfigWithFrequency,
  useUpdatePaymentConfigMutation,
} from '@/services/paymentConfigApi';
import { useGetMerchantConfigsByAMCQuery } from '@/services/gatewayConfigApi';

const parseTags = (tags?: string): string[] =>
  (tags ?? '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const TagChips = ({ tags }: { tags?: string }) => {
  const parsed = parseTags(tags);
  if (parsed.length === 0) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
      {parsed.map((tag) => (
        <Chip key={tag} label={tag} size="small" variant="outlined" />
      ))}
    </Stack>
  );
};

export const StrictModeDroppable = ({ children, ...props }: DroppableProps) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animation = requestAnimationFrame(() => setEnabled(true));

    return () => {
      cancelAnimationFrame(animation);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};
interface MerchantConfigModalProps {
  open: boolean;
  onClose: () => void;
  paymentConfigId: string;
  amcId: string;
  gatewaySelectionAlgo: 'Priority' | 'Percentage';
  initialConfigs: MerchantConfigWithFrequency[];
  onConfigsChange?: (configs: MerchantConfigWithFrequency[]) => void;
  filterByFundCode?: string;
}

// Custom styled switch with blue track when checked
const PrioritySwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  '& .MuiSwitch-switchBase': {
    margin: 1,
    padding: 0,
    transform: 'translateX(6px)',
    '&.Mui-checked': {
      color: theme.palette.common.white,
      transform: 'translateX(22px)',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.common.white,
    width: 32,
    height: 32,
    '&:before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor:
      theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[400],
    borderRadius: 20 / 2,
  },
}));

export default function MerchantConfigModal({
  open,
  gatewaySelectionAlgo,
  onClose,
  paymentConfigId,
  amcId,
  initialConfigs,
  onConfigsChange,
  filterByFundCode,
}: MerchantConfigModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const [selectedConfigs, setSelectedConfigs] = useState<MerchantConfigWithFrequency[]>([]);
  const [frequencyError, setFrequencyError] = useState('');
  const [isPriorityBased, setIsPriorityBased] = useState(gatewaySelectionAlgo === 'Priority');
  // Removed unused availableConfigsList state
  const [sortedConfigs, setSortedConfigs] = useState<MerchantConfigWithFrequency[]>([]);
  const { data: availableConfigs = [] } = useGetMerchantConfigsByAMCQuery(amcId);
  const [updatePaymentConfig] = useUpdatePaymentConfigMutation();

  // Filter merchant configs by fund code if specified
  const filteredAvailableConfigs = useMemo(() => {
    return filterByFundCode
      ? availableConfigs.filter((config) => config.fundCode === filterByFundCode)
      : availableConfigs;
  }, [availableConfigs, filterByFundCode]);

  useEffect(() => {
    if (open) {
      setSelectedConfigs(initialConfigs);
      setSortedConfigs(
        filteredAvailableConfigs.map((config, index) => ({ ...config, priority: index + 1 }))
      );
    }
  }, [open, initialConfigs, filteredAvailableConfigs]);

  const handleFrequencyChange = (id: string, value: string) => {
    // Skip for priority-based mode
    if (isPriorityBased) return;

    // Validate input is a number and within bounds
    const numValue = Math.max(0, Math.min(100, Number(value) || 0));

    const updated = selectedConfigs.map((config) =>
      config.id === id ? { ...config, frequency: numValue } : config
    );
    setSelectedConfigs(updated);

    // Validate total in real-time
    const total = updated.reduce((sum, config) => sum + (config.frequency ?? 0), 0);
    if (total > 100) {
      setFrequencyError('Total frequency cannot exceed 100%');
    } else if (total < 100) {
      setFrequencyError('Total frequency is currently ' + total + '%');
    } else {
      setFrequencyError('');
    }
  };

  const handleToggleConfig = (config: MerchantConfigWithFrequency) => {
    setSelectedConfigs((prev) => {
      const exists = prev.some((c) => c.id === config.id);
      if (exists) {
        return prev.filter((c) => c.id !== config.id);
      }

      if (isPriorityBased) {
        // In priority mode, just add to the end of the list
        return [...prev, { ...config, frequency: 0 }]; // Frequency doesn't matter in priority mode
      } else {
        // Percentage-based mode logic
        const newFrequency = prev.length === 0 ? 100 : Math.floor(100 / (prev.length + 1));

        // Adjust existing frequencies to make room for the new one
        if (prev.length > 0) {
          const remainingPercentage = 100 - newFrequency;
          const adjustedPrev = prev.map((c) => ({
            ...c,
            frequency: Math.floor(((c.frequency ?? 0) * remainingPercentage) / 100),
          }));
          return [...adjustedPrev, { ...config, frequency: newFrequency }];
        }

        return [...prev, { ...config, frequency: newFrequency }];
      }
    });
  };

  const validateFrequencies = () => {
    // Skip validation for priority-based mode
    if (isPriorityBased) {
      setFrequencyError('');
      return true;
    }

    const total = selectedConfigs.reduce((sum, config) => sum + (config.frequency ?? 0), 0);
    if (total !== 100) {
      setFrequencyError('Frequencies must sum to 100%');
      return false;
    }
    setFrequencyError('');
    return true;
  };

  const handleSave = async () => {
    if (!isPriorityBased && !validateFrequencies()) return;

    try {
      // If priority-based, we need to add priority values based on order
      const configsToSave = isPriorityBased
        ? sortedConfigs.map((config, index) => ({
            ...config,
            // Use index as priority (lower index = higher priority)
            priority: index + 1,
          }))
        : selectedConfigs;

      // If onConfigsChange is provided (for Add dialog), call it instead of updating
      if (onConfigsChange) {
        onConfigsChange(configsToSave);
        onClose();
        return;
      }

      await updatePaymentConfig({
        id: paymentConfigId,
        merchantConfigs: configsToSave,
        gatewaySelectionAlgo: isPriorityBased ? 'Priority' : 'Percentage',
      }).unwrap();
      enqueueSnackbar('Payment config updated successfully', { variant: 'success' });
      onClose();
    } catch (err) {
      enqueueSnackbar('Failed to update payment config', { variant: 'error' });
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result: any) => {
    // Dropped outside the list
    if (!result.destination) return;

    const items = Array.from(sortedConfigs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSortedConfigs(items.map((config, index) => ({ ...config, priority: index + 1 })));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Merchant Configuration</DialogTitle>
      <DialogContent dividers>
        {frequencyError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {frequencyError}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ mr: 2, fontWeight: 'bold' }}>Percentage Based</Typography>
          <PrioritySwitch
            checked={isPriorityBased}
            onChange={(e) => {
              setIsPriorityBased(e.target.checked);
              // Clear any frequency errors when switching modes
              setFrequencyError('');
            }}
          />
          <Typography sx={{ ml: 2, fontWeight: 'bold' }}>Priority Based</Typography>
        </Box>

        {isPriorityBased && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drag items to reorder. Higher items have higher priority.
          </Typography>
        )}

        <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 1, mb: 2 }}>
          {!isPriorityBased ? (
            // Percentage-based mode - Regular list with checkboxes
            <List dense>
              {filteredAvailableConfigs.map((config, index) => {
                const isSelected = selectedConfigs.some((c) => c.id === config.id);
                const selectedConfig = selectedConfigs.find((c) => c.id === config.id);
                const logo = getAggregatorLogo(config.aggregator || '');

                return (
                  <ListItem
                    key={config.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      ...(index === availableConfigs.length - 1
                        ? {}
                        : { borderBottom: `1px solid ${theme.palette.divider}` }),
                      py: 1,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        onChange={() => handleToggleConfig(config)}
                        inputProps={{ 'aria-labelledby': `checkbox-${config.id}` }}
                      />
                    </ListItemIcon>
                    <img
                      src={logo}
                      alt={config.aggregator}
                      height="20"
                      style={{ marginRight: 10 }}
                    />
                    <ListItemText
                      primary={
                        AGGREGATOR_OPTIONS.find((option) => option.value === config.aggregator)
                          ?.label
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                      secondary={
                        <>
                          {config.merchantId}
                          <TagChips tags={config.tags} />
                        </>
                      }
                    />
                    {isSelected && (
                      <TextField
                        size="small"
                        type="number"
                        value={selectedConfig?.frequency ?? 0}
                        onChange={(e) => handleFrequencyChange(config.id || '', e.target.value)}
                        InputProps={{
                          endAdornment: <Typography variant="caption">%</Typography>,
                        }}
                        inputProps={{
                          min: 0,
                          max: 100,
                          step: 1,
                        }}
                        sx={{ width: 100, ml: 'auto', mr: 2, justifyContent: 'center' }}
                      />
                    )}
                  </ListItem>
                );
              })}
            </List>
          ) : (
            // Priority-based mode - Draggable list with drag handles
            <DragDropContext onDragEnd={handleDragEnd}>
              <StrictModeDroppable droppableId="droppable">
                {(provided) => (
                  <List dense {...provided.droppableProps} ref={provided.innerRef}>
                    {sortedConfigs.map((config, index) => {
                      const logo = getAggregatorLogo(config.aggregator || '');

                      return (
                        <Draggable key={config.id} draggableId={config.id!} index={index}>
                          {(provided, snapshot) => (
                            <ListItem
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                ...(index === selectedConfigs.length - 1
                                  ? {}
                                  : { borderBottom: `1px solid ${theme.palette.divider}` }),
                                py: 1,
                                backgroundColor: snapshot.isDragging
                                  ? theme.palette.action.hover
                                  : 'transparent',
                                '&:hover': { backgroundColor: theme.palette.action.hover },
                              }}
                            >
                              <ListItemIcon sx={{ minWidth: 40 }} {...provided.dragHandleProps}>
                                <DragIndicator color="action" />
                              </ListItemIcon>
                              <img
                                src={logo}
                                alt={config.aggregator}
                                height="20"
                                style={{ marginRight: 10 }}
                              />
                              <ListItemText
                                primary={
                                  AGGREGATOR_OPTIONS.find(
                                    (option) => option.value === config.aggregator
                                  )?.label
                                }
                                secondaryTypographyProps={{ component: 'div' }}
                                secondary={
                                  <>
                                    {config.merchantId}
                                    <TagChips tags={config.tags} />
                                  </>
                                }
                              />
                              <Typography sx={{ ml: 'auto', mr: 2, color: 'text.secondary' }}>
                                Priority: {config.priority}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setSelectedConfigs((prev) =>
                                    prev.filter((c) => c.id !== config.id)
                                  )
                                }
                                sx={{ mr: 1 }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </ListItem>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                    {selectedConfigs.length === 0 && !isPriorityBased && (
                      <Typography color="text.secondary" align="center" py={2}>
                        No aggregators selected. Use the checkboxes to select aggregators.
                      </Typography>
                    )}
                  </List>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          )}
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
